import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  Container, Stepper, Step, StepLabel, IconButton, InputAdornment,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { tenantAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const steps = ['School Information', 'Admin Account'];

const RegisterSchool = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    school_name: '',
    tenant_code: '',
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    admin_password_confirmation: '',
  });

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const validateStep = () => {
    setError('');
    if (activeStep === 0) {
      if (!formData.school_name.trim()) return setError('School name is required') || false;
      if (!formData.tenant_code.trim()) return setError('School ID is required') || false;
      if (formData.tenant_code.length < 3) return setError('School ID must be at least 3 characters') || false;
      return true;
    }
    if (activeStep === 1) {
      if (!formData.admin_name.trim()) return setError('Admin name is required') || false;
      if (!formData.admin_email.trim()) return setError('Admin email is required') || false;
      if (!formData.admin_password) return setError('Password is required') || false;
      if (formData.admin_password.length < 8) return setError('Password must be at least 8 characters') || false;
      if (formData.admin_password !== formData.admin_password_confirmation) return setError('Passwords do not match') || false;
      return true;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setError('');

    try {
      await tenantAPI.provision({
        school_name:    formData.school_name,
        tenant_code:    formData.tenant_code,
        admin_name:     formData.admin_name,
        admin_email:    formData.admin_email,
        admin_phone:    formData.admin_phone || null,
        admin_password: formData.admin_password,
      });
      setSuccess(formData.tenant_code);
      const timer = setTimeout(() => navigate('/login'), 5000);
      return () => clearTimeout(timer);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        const firstError = Object.values(data.errors)[0];
        setError(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        setError(data?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 2 } }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
          <Card sx={{ width: '100%', maxWidth: 450 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" fontWeight={600} gutterBottom>
                🎉 Registration Successful!
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
                Your school has been registered.
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Your School ID is:
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', borderRadius: 2, p: 2, mb: 3 }}>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {success}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Use this School ID when logging in. You'll be redirected to the login page shortly.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 1, sm: 2 } }}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
              <Box component="img" src="/Logo.png" alt="School Sync Logo" sx={{ width: 80, height: 80, objectFit: 'contain', mb: 1 }} />
              <Typography variant="h5" fontWeight={600} color="primary">
                Register Your School
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Set up your school on School Sync
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={activeStep === 1 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
              {activeStep === 0 && (
                <>
                  <TextField
                    fullWidth
                    label="School Name"
                    placeholder="e.g. St. Mary's Secondary School"
                    value={formData.school_name}
                    onChange={handleChange('school_name')}
                    required
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="School ID"
                    placeholder="e.g. StMarys001"
                    value={formData.tenant_code}
                    onChange={(e) => {
                      // Strip spaces and symbols — allow letters (upper+lower), numbers, dashes, underscores
                      const val = e.target.value.replace(/[^A-Za-z0-9_-]/g, '');
                      setFormData({ ...formData, tenant_code: val });
                    }}
                    required
                    margin="normal"
                    helperText="Letters, numbers, dashes and underscores only. Used to log in."
                  />
                </>
              )}

              {activeStep === 1 && (
                <>
                  <TextField
                    fullWidth
                    label="Admin Full Name"
                    placeholder="e.g. John Doe"
                    value={formData.admin_name}
                    onChange={handleChange('admin_name')}
                    required
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    type="email"
                    label="Admin Email"
                    placeholder="e.g. admin@school.com"
                    value={formData.admin_email}
                    onChange={handleChange('admin_email')}
                    required
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Admin Phone (optional)"
                    placeholder="e.g. 0772123456"
                    value={formData.admin_phone}
                    onChange={handleChange('admin_phone')}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    value={formData.admin_password}
                    onChange={handleChange('admin_password')}
                    required
                    margin="normal"
                    helperText="Minimum 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    value={formData.admin_password_confirmation}
                    onChange={handleChange('admin_password_confirmation')}
                    required
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </>
              )}

              <Box display="flex" justifyContent="space-between" mt={3}>
                {activeStep > 0 ? (
                  <Button onClick={handleBack} disabled={loading}>
                    Back
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/login')} disabled={loading}>
                    Back to Login
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : activeStep === 1 ? 'Register School' : 'Next'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default RegisterSchool;
